export class MachineComparison extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return

    this.abortController = new AbortController()
    this.mobileMedia = window.matchMedia('(max-width: 699px)')
    this.machineSelects = [...this.querySelectorAll('[data-machine-select]')]
    this.navLinks = [...this.querySelectorAll('[data-machine-nav-link]')]
    this.groupTargets = [...this.querySelectorAll('[data-machine-group-target]')]
    this.selectionBySlot = new Map(
      this.machineSelects.map((select) => [select.dataset.slot, select.value]),
    )

    for (const select of this.machineSelects) {
      select.addEventListener('change', () => this.handleSelectionChange(select), {
        signal: this.abortController.signal,
      })
    }

    this.handleBlockSelect = (event) => this.handleEditorBlockSelect(event)
    this.addEventListener('shopify:block:select', this.handleBlockSelect, {
      signal: this.abortController.signal,
    })

    this.handleViewportChange = () => this.syncViewport()
    this.mobileMedia.addEventListener('change', this.handleViewportChange)
    this.connectGroupNavigation()
    this.syncViewport()
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.groupObserver?.disconnect()
    this.abortController = null
    this.groupObserver = null
    this.mobileMedia?.removeEventListener('change', this.handleViewportChange)
    this.mobileMedia = null
    this.handleViewportChange = null
    this.handleBlockSelect = null
    this.machineSelects = null
    this.navLinks = null
    this.groupTargets = null
    this.selectionBySlot = null
  }

  connectGroupNavigation() {
    for (const link of this.navLinks) {
      const activate = () => this.syncActiveGroup(link.dataset.machineGroup)
      link.addEventListener('click', activate, { signal: this.abortController.signal })
      link.addEventListener('focus', activate, { signal: this.abortController.signal })
    }

    this.syncActiveGroup(this.groupTargets[0]?.dataset.machineGroup)

    if (typeof window.IntersectionObserver !== 'function') return

    this.groupObserver = new window.IntersectionObserver(
      (entries) => {
        const activeEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (first, second) =>
              Math.abs(first.boundingClientRect.top) - Math.abs(second.boundingClientRect.top),
          )[0]
        if (activeEntry) this.syncActiveGroup(activeEntry.target.dataset.machineGroup)
      },
      {
        root: null,
        rootMargin: '-15% 0px -70% 0px',
        threshold: [0, 0.5, 1],
      },
    )

    for (const target of this.groupTargets) this.groupObserver.observe(target)
  }

  syncActiveGroup(group) {
    if (!group) return

    for (const link of this.navLinks ?? []) {
      if (link.dataset.machineGroup === group) link.setAttribute('aria-current', 'location')
      else link.removeAttribute('aria-current')
    }
  }

  handleSelectionChange(select) {
    if (select.disabled) return

    const otherValues = new Set(
      this.activeSelects()
        .filter((candidate) => candidate !== select)
        .map((candidate) => candidate.value),
    )
    const previousValue = this.selectionBySlot.get(select.dataset.slot)
    const isValid = this.hasPanel(select, select.value) && !otherValues.has(select.value)

    if (!isValid) {
      if (this.hasPanel(select, previousValue) && !otherValues.has(previousValue)) {
        select.value = previousValue
      } else {
        select.value = this.firstUnusedValue(select, otherValues)
      }
    }

    this.syncSlot(select)
    this.selectionBySlot.set(select.dataset.slot, select.value)
    this.syncDisabledOptions()
  }

  handleEditorBlockSelect(event) {
    const blockId = event.detail?.blockId
    if (!blockId) return

    const activeSelects = this.activeSelects()
    const selectedSlot = activeSelects.find((select) => select.value === blockId)
    const targetSelect = selectedSlot ?? activeSelects[0]
    if (!targetSelect || !this.hasPanel(targetSelect, blockId)) return

    targetSelect.value = blockId
    this.handleSelectionChange(targetSelect)
  }

  syncSlot(select) {
    const slot = select.dataset.slot
    const key = select.value
    const panels = [...this.querySelectorAll('[data-machine-panel]')].filter(
      (panel) => panel.dataset.slot === slot,
    )

    if (!panels.some((panel) => panel.dataset.machineKey === key)) return

    for (const panel of panels) {
      const visible = panel.dataset.machineKey === key
      panel.hidden = !visible
      if (visible) panel.removeAttribute('aria-hidden')
      else panel.setAttribute('aria-hidden', 'true')
    }
  }

  activeSelects() {
    return this.machineSelects.filter((select) => !select.disabled)
  }

  hasPanel(select, key) {
    if (!key) return false
    return [...this.querySelectorAll('[data-machine-panel]')].some(
      (panel) => panel.dataset.slot === select.dataset.slot && panel.dataset.machineKey === key,
    )
  }

  firstUnusedValue(select, usedValues) {
    const option = [...select.options].find(
      (candidate) => !usedValues.has(candidate.value) && this.hasPanel(select, candidate.value),
    )
    return option?.value ?? ''
  }

  normalizeActiveSelections() {
    const usedValues = new Set()

    for (const select of this.activeSelects()) {
      if (!this.hasPanel(select, select.value) || usedValues.has(select.value)) {
        select.value = this.firstUnusedValue(select, usedValues)
      }
      if (select.value) usedValues.add(select.value)
      this.syncSlot(select)
      this.selectionBySlot.set(select.dataset.slot, select.value)
    }
  }

  syncDisabledOptions() {
    const activeSelects = this.activeSelects()

    for (const select of this.machineSelects) {
      for (const option of select.options) {
        option.disabled = activeSelects.some(
          (otherSelect) => otherSelect !== select && otherSelect.value === option.value,
        )
      }
    }
  }

  syncViewport() {
    for (const select of this.machineSelects) {
      const isEligibleSlot = select.dataset.machineSlotEligible !== 'false'
      const isHiddenByViewport = select.dataset.slot === '3' && this.mobileMedia?.matches === true
      select.disabled = !isEligibleSlot || isHiddenByViewport
    }
    this.normalizeActiveSelections()
    this.syncDisabledOptions()
  }
}

if (!customElements.get('machine-comparison')) {
  customElements.define('machine-comparison', MachineComparison)
}
